'use client';
import {
  MobileHeaderBackButton,
  Title,
  IConversationFansResponse,
  Avatar,
  IFanConversationSet,
} from '@ui/components';
import { usePostHogFeatureFlag } from '@ui/hooks';
import { useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { GroupInfoModal } from './group-info-modal';

interface IMobileHeaderContent {
  onBack(): void;
  title: string;
  subTitle?: string;
  leftItem?: JSX.Element;
  titleClassName?: string;
  subTitleClassName?: string;
  backStroke?: string;
  theme?: 'light' | 'dark';
  showLogo?: boolean;
  fansData?: IConversationFansResponse;
  isFanAllowedToReply?: boolean;
  conversationId?: string;
  conversationData?: IFanConversationSet | null;
  useGetConversationFansQuery?: any;
  currentUserId?: string;
  backIconType?: 'back' | 'close';
}
export function MobileHeaderContent({
  title,
  onBack,
  leftItem,
  titleClassName,
  backStroke,
  theme = 'light',
  subTitle,
  subTitleClassName,
  showLogo,
  fansData,
  isFanAllowedToReply,
  conversationId,
  conversationData,
  useGetConversationFansQuery,
  currentUserId,
  backIconType = 'back',
}: IMobileHeaderContent) {
  const isConversationCreator = useMemo(
    () => conversationData?.conversation?.creator_id === currentUserId,
    [conversationData?.conversation?.creator_id, currentUserId],
  );
  const isDark = theme === 'dark';
  const [isGroupInfoModalOpen, setIsGroupInfoModalOpen] = useState(false);

  const isDirectMessage = useMemo(
    () => conversationData?.conversation?.conversation_type === 'direct_messages',
    [conversationData?.conversation?.conversation_type],
  );
  const isGroupMessage = useMemo(() => {
    return fansData && fansData?.fans && fansData?.fans?.length > 1 && isFanAllowedToReply;
  }, [fansData?.fans.length, isFanAllowedToReply]);
  const isMessageRecipientsFlagEnabled = usePostHogFeatureFlag('message-recipient');

  const handleGroupInfoClick = () => {
    if (conversationId) {
      setIsGroupInfoModalOpen(true);
    }
  };

  const profileImage = useMemo(() => {
    let image = '';

    if (conversationData?.conversation.conversation_type === 'direct_messages') {
      const participants = [
        ...(conversationData?.direct_message_participants || [])?.map((participant) => ({
          id: participant.reference_id,
          avatar_url: participant.avatar_url,
        })),
        ...(fansData?.fans || [])?.map((fan) => ({ id: fan.id, avatar_url: fan.avatar_url })),
      ];

      const otherParticipant = participants?.find(
        (participant) => participant?.id !== currentUserId,
      );
      image = otherParticipant?.avatar_url || '';
    } else {
      image = conversationData?.community_details?.creator_avatar_url || '';
    }

    return image;
  }, [
    isDirectMessage,
    isConversationCreator,
    fansData?.fans,
    conversationData?.community_details?.creator_avatar_url,
    conversationData?.direct_message_participants,
    currentUserId,
  ]);

  return (
    <>
      <div className="basis-1/5">
        <MobileHeaderBackButton
          isDark={isDark}
          onBack={onBack}
          backStroke={backStroke}
          showLogo={showLogo}
          iconType={backIconType}
        />
      </div>
      <div className="w-full">
        <Title
          type="sm"
          className={twMerge(
            'text-center font-bold flex items-center justify-center gap-2 w-fit',
            isDark ? 'text-white' : '',
            titleClassName,
          )}>
          {profileImage && (
            <Avatar
              src={profileImage}
              displayName={title}
              size="xs"
              className="h-[44px] w-[44px] shrink-0"
            />
          )}
          <span className="text-left font-bold truncate  md:max-w-[250px] max-w-[140px]">
            {title}
          </span>
        </Title>
        {subTitle && (
          <Title
            type="xs"
            className={twMerge(
              'text-center text-xs md:text-sm w-[170px] overflow-hidden text-ellipsis text-nowrap',
              isDark ? 'text-white' : '',
              subTitleClassName,
            )}>
            {subTitle}
          </Title>
        )}
      </div>

      {isGroupMessage && isMessageRecipientsFlagEnabled && (
        <>
          <div
            className="border-note flex w-fit items-center justify-center backdrop-blur-lg cursor-pointer hover:bg-gray-50 transition-colors rounded-lg"
            onClick={handleGroupInfoClick}>
            <div className="flex flex-col md:flex-row items-start md:items-center ">
              <div className="flex -space-x-2 overflow-hidden shrink-0">
                {fansData?.fans?.slice(0, 3).map((fan) => (
                  <Avatar
                    key={fan?.id}
                    src={fan?.avatar_url}
                    displayName={fan?.display_name}
                    size="xs"
                    className="border border-gray-100 shrink-0"
                  />
                ))}
              </div>
              {fansData?.fans && fansData?.fans?.length >= 3 && (
                <div className="text-xs font-medium text-note">
                  {fansData?.total_records > 5 && <span>+{fansData?.total_records - 3} more </span>}
                </div>
              )}
            </div>
          </div>
          {conversationId && useGetConversationFansQuery && (
            <GroupInfoModal
              isOpen={isGroupInfoModalOpen}
              onClose={() => setIsGroupInfoModalOpen(false)}
              conversationId={conversationId}
              conversationData={conversationData}
              conversationName={title}
              useGetConversationFansQuery={useGetConversationFansQuery}
            />
          )}
        </>
      )}

      <div className="flex items-center justify-end">
        {leftItem && <div className="mr-2">{leftItem}</div>}
      </div>
    </>
  );
}
